import { type ModalProps } from '~/util';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '~/primitives/sheet';
import { useAppForm } from '~/util/hooks/useAppForm';
import { formOptions } from '@tanstack/react-form';
import { useApiMutation } from '~/util/hooks/useApiMutation';

import { getRouteApi } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
    changeMemberRoleMutation,
    kickMemberMutation,
} from '~/util/mutations/public/clans';
import { ClanMember } from '~/util/types/clans';
import { Button } from '~/primitives/button';
import { LogOut } from 'lucide-react';
import ConfirmModal from '~/components/ConfirmModal';
import { clanMemberFormInput, clanMemberFormSchema } from '~/util/forms/clan';
import { UserProfilePicture } from '~/components/UserProfilePicture';

const Route = getRouteApi('/profile/clan/$clanid');

export const EditClanMemberSheet = ({
    member,
    ...modalProps
}: { member: ClanMember } & ModalProps) => {
    const { clanid } = Route.useParams();

    const memberRoleMutation = useApiMutation(changeMemberRoleMutation());
    const memberKickMutation = useApiMutation(kickMemberMutation());

    const [confirmOpen, setConfirmOpen] = useState(false);

    const currentMember = useMemo(
        () => ({
            role: String(member.role) as '0' | '1' | '2',
        }),
        [member],
    ) as clanMemberFormInput;

    const clanMemberForm = useAppForm(
        formOptions({
            formId: 'edit-clan-member-staff',
            defaultValues: currentMember,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: clanMemberFormSchema,
            },
            onSubmit: async ({ value }) => {
                memberRoleMutation.mutate({
                    clanid,
                    member,
                    newRole: clanMemberFormSchema.parse(value).role,
                });
            },
        }),
    );

    useEffect(() => {
        clanMemberForm.reset(currentMember);
    }, [clanMemberForm, currentMember]);

    return (
        <>
            <Sheet {...modalProps}>
                <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:max-w-150 overflow-y-scroll">
                    <SheetHeader>
                        <SheetTitle>Edit Clan Member</SheetTitle>
                        <SheetDescription>
                            Update member role or remove them from the clan.
                        </SheetDescription>
                    </SheetHeader>

                    <form
                        id="edit-clan-member-staff"
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            clanMemberForm.handleSubmit();
                        }}
                    >
                        <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                            <h2 className="flex items-center">
                                <UserProfilePicture
                                    user={member}
                                    className="size-15 mr-2"
                                    smallBadge
                                />
                                {member.global_name}
                            </h2>

                            <clanMemberForm.AppField name="role">
                                {(field) => (
                                    <field.DropdownField
                                        label="Role"
                                        options={['0', '1', '2']}
                                        optionsDisplay={[
                                            'Member',
                                            'Vice-Leader',
                                            'Leader',
                                        ]}
                                        required
                                        placeholder="Select a role"
                                    />
                                )}
                            </clanMemberForm.AppField>
                        </div>

                        <SheetFooter className="sm:justify-center">
                            <div className="flex w-full gap-2">
                                <clanMemberForm.AppForm>
                                    <clanMemberForm.SubscribeButton
                                        label="Edit member"
                                        classname="w-2/3"
                                    />
                                </clanMemberForm.AppForm>
                                <Button
                                    type="button"
                                    variant="destructive"
                                    className="w-1/3"
                                    onClick={() => {
                                        setConfirmOpen(true);
                                    }}
                                >
                                    <LogOut /> Kick Member
                                </Button>
                            </div>
                        </SheetFooter>
                    </form>
                    <ConfirmModal
                        onConfirm={() =>
                            memberKickMutation.mutate({
                                clanid,
                                member,
                            })
                        }
                        open={confirmOpen}
                        onOpenChange={setConfirmOpen}
                        buttonText="Kick"
                        buttonVariant="destructive"
                        title="Confirm kick"
                        text={`Are you sure you want to kick ${member.global_name} from the clan?`}
                    />
                </SheetContent>
            </Sheet>
        </>
    );
};
